import React, { useState, useContext, useEffect } from 'react';
import SocketContext from '../../../../../../../NewWebSocket/components/WebSocketAdapter';
import './RoleSelector.css';

// Role options matching backend ChampionRole enum
const ROLES = [
  { value: 'top', label: 'Top', description: 'Level cap raised to 20, +600 XP at quest completion, +12.5% bonus XP' },
  { value: 'jungle', label: 'Jgl', description: 'Jungle role (quest rewards TBD)' },
  { value: 'mid', label: 'Mid', description: 'Access to Tier 3 boots at quest completion' },
  { value: 'bot', label: 'Bot', description: '+300g, +2g per minion, +50g per takedown, 7th item slot for boots' },
  { value: 'support', label: 'Sup', description: 'Support role (quest rewards TBD)' },
];

interface RoleSelectorProps {
  championId: string;
  currentRole: string;
  questCompleted?: boolean;
  onRoleUpdate?: (role: string) => void;
  onQuestCompletedUpdate?: (completed: boolean) => void;
}

const RoleSelector: React.FC<RoleSelectorProps> = ({ 
  championId, 
  currentRole, 
  questCompleted = true,
  onRoleUpdate,
  onQuestCompletedUpdate 
}) => {
  const socket = useContext(SocketContext);
  const [selectedRole, setSelectedRole] = useState(currentRole || 'mid');
  const [isQuestCompleted, setIsQuestCompleted] = useState(questCompleted);

  // Update local state when props change
  useEffect(() => {
    if (currentRole) {
      setSelectedRole(currentRole);
    }
  }, [currentRole]);

  useEffect(() => {
    setIsQuestCompleted(questCompleted);
  }, [questCompleted]);

  const handleRoleChange = (newRole: string) => {
    setSelectedRole(newRole);
    
    // Use callback if provided (static mode), otherwise use socket (WebSocket mode)
    if (onRoleUpdate) {
      onRoleUpdate(newRole);
    } else if (socket) {
      socket.emit('set-role', { championId, role: newRole });
    }
  };

  const handleQuestCompletedChange = (completed: boolean) => {
    setIsQuestCompleted(completed);
    
    if (onQuestCompletedUpdate) {
      onQuestCompletedUpdate(completed);
    } else if (socket) {
      socket.emit('set-role-quest-completed', { championId, completed });
    }
  };

  const selectedRoleInfo = ROLES.find(r => r.value === selectedRole);

  return (
    <div className="control-container role-selector">
      <div className="control-header">
        <span className="control-label">Role Quest</span>
        <label className="quest-completed-toggle" title="Toggle quest completion status">
          <input
            type="checkbox"
            checked={isQuestCompleted}
            onChange={(e) => handleQuestCompletedChange(e.target.checked)}
          />
          <span className="toggle-label">Completed</span>
        </label>
      </div>
      <div className="role-buttons">
        {ROLES.map((role) => (
          <button
            key={role.value}
            className={`role-button ${selectedRole === role.value ? 'active' : ''}`}
            onClick={() => handleRoleChange(role.value)}
            title={role.description}
          >
            {role.label}
          </button>
        ))}
      </div>
      {selectedRoleInfo && (
        <div className={`role-description ${!isQuestCompleted ? 'quest-incomplete' : ''}`}>
          {isQuestCompleted 
            ? selectedRoleInfo.description 
            : `Quest incomplete - ${selectedRoleInfo.description}`}
        </div>
      )}
    </div>
  );
};

export default RoleSelector;
